import {
    Card, Spin, Table,
} from 'antd';
import {TablePaginationConfig} from 'antd/es/table';
import cn from 'classnames';
import React, {
    FC,
    Key,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {useReactToPrint} from 'react-to-print';

import {useTableColumnFilters} from 'components/@common/widgets/custom-table/table-column-filter/use-table-column-filter/use-table-column-filter';
import {useTableColumnSort} from 'components/@common/widgets/custom-table/table-column-menu';
import {RecordsInfo} from 'components/@common/widgets/records-info/records-info';
import {Spinner} from 'components/spinner';
import {useBreadcrumbsController} from 'components/table-report/hooks/use-breadcrumbs-controller';
import {renderColumns} from 'components/table-report/table-report-columns';
import {UIBlocker} from 'components/ui-blocker';
import {HorizontalScroll} from 'features/horizontal-scroll';
import {CONTAINER_SELECTORS} from 'features/horizontal-scroll/model/constants';
import {selectIsFileLoading} from 'features/provide-custom-link';
import {selectContextRawData} from 'modules/context/context-selector';
import {ReactComponent as ArrowRightIcon} from 'shared/assets/arrow-right.svg';
import {useAfterEffect} from 'shared/hooks/use-after-effect';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {useUserRoleFunctions, useUserRoleFunctionsByDocId} from 'shared/hooks/use-user-role-functions';
import {getPrefixedSection} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {removeExtraPartFromEntityName} from 'store/slices/documents-uploading-slice';
import {selectIsThunkPending, selectThunkError} from 'store/slices/loading-state-slice';
import {
    EReportConfigurationPosition,
    IReportConfigurationReportGroupDto,
} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {tableReportSliceActions} from 'store/slices/table-report-slice';
import {selectTableReportColumnFiltersData} from 'store/slices/table-report-slice/table-report-column-filters/table-report-column-filters-selectors';
import {
    selectLastTableReportLoadPageRequestArgs,
    selectTableReportAsideMenu,
    selectTableReportAttributes,
    selectTableReportAttributesAsRecords,
    selectTableReportCurrentColumns,
    selectTableReportData,
    selectTableReportDataSource,
    selectTableReportExcelTableHeaderStructure,
    selectTableReportTemplateConfig,
    selectTableReportUserReportGroupsSettings,
} from 'store/slices/table-report-slice/table-report-slice-selectors';
import {loadTableReportPageData} from 'store/slices/table-report-slice/table-report-slice-thunks';
import {ITableReportDrillDown} from 'store/slices/table-report-slice/table-report-slice-types';
import {selectTableReportSortData} from 'store/slices/table-report-slice/table-report-sort/table-report-sort-selectors';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {Breadcrumbs, BreadcrumbsContext} from '../breadcrumbs';
import {Aside} from './components/aside/aside';
import {DrillDownModal} from './components/dd-modal/dd-modal';
import {TableReportActions} from './components/table-report-actions/table-report-actions';
import {TableReportHeader} from './components/table-report-header/table-report-header';
import {useControl} from './hooks/use-control';
import {useStickyTableHeader} from './hooks/use-sticky-table-header';
import {defaultPageSizeOptions} from './mocks/data';
import {EReportViewMode, ITableReportProps} from './table-report-types';
import {tableReportColumnContentUtils} from './utils';

import './table-report.less';

export const TableReport: FC<ITableReportProps> = (
    {
        entityName,
        docId: initialDocId,
        templateCode: initialTemplateCode,
    }: ITableReportProps,
) => {
    const history = useAppHistory();
    const {resetDocIdsForRequest} = tableReportSliceActions;
    const isFileLoading = useAppSelector(selectIsFileLoading);

    const dispatch = useAppDispatch();

    const {organizationId, taxPeriodId} = useAppSelector(selectContextRawData) ?? {};
    const tableContentRef = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => tableContentRef.current,
    });

    const breadcrumbs = history.currentState?.[ELocationStateKey.TABLE_REPORT_BREADCRUMBS];

    const [docId, setDocId] = useState<string | null>(initialDocId);
    const [templateCode, setTemplateCode] = useState<string>(initialTemplateCode);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [chosenMode, setChosenMode] = useState<string>(EReportViewMode.DETAILED);

    const reportTemplateConfig = useAppSelector(selectTableReportTemplateConfig);
    const {
        rowFormats = [],
        reservedColumns,
        enabledRowAttachments,
        enabledRowComments,
        enabledRowRequests,
        designTemplate,
        paginationPosition,
        reportGroups,
        enabledMenu,
        enabledSelectAllRowsBtn,
        enabledDrillUpBtn,
        enabledRecordsInfo,
    } = reportTemplateConfig ?? {};

    const userReportSettings = useAppSelector(selectTableReportUserReportGroupsSettings);
    const userReportGroups = userReportSettings?.reportGroups
        && userReportSettings.reportGroups.length > 0
        && userReportSettings.reportGroups as unknown as {[p: string]: IReportConfigurationReportGroupDto} | undefined;

    const {setSortData, setTableColumnFilters} = tableReportSliceActions;

    const excelTableHeaderStructure = useAppSelector(selectTableReportExcelTableHeaderStructure);

    const tableReportData = useAppSelector(selectTableReportData);
    const asideMenuStructure = useAppSelector(selectTableReportAsideMenu);
    const dataSource = useAppSelector(selectTableReportDataSource);
    const lastRequestParams = useAppSelector(selectLastTableReportLoadPageRequestArgs);

    const {
        tableRef,
        isTableHeaderPinned,
        handlePinButtonClick,
        setIsTableHeaderPinned,
    } = useStickyTableHeader(!!(enabledMenu && asideMenuStructure?.length));

    const drillDowns = tableReportData?.drilldown;

    const tableReportAttributes = useAppSelector(selectTableReportAttributesAsRecords);

    const isLoadingPage = useAppSelector(s => selectIsThunkPending(s, loadTableReportPageData.typePrefix));
    const loadTablePageError = useAppSelector(s => selectThunkError(s, loadTableReportPageData.typePrefix));

    useEffect(() => {
        if (docId !== initialDocId || templateCode !== initialTemplateCode) {
            setTemplateCode(initialTemplateCode);
            setDocId(initialDocId);
        }
    }, [initialDocId, initialTemplateCode]);

    useEffect(() => () => { dispatch(resetDocIdsForRequest()); }, []);

    const [scale, setScale] = useState<number>(1);
    const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
    const [isDDModalVisible, setIsDDModalVisible] = useState(false);
    const [ddModalEntries, setDDModalEntries] = useState<ITableReportDrillDown[]>();
    const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(asideMenuStructure === null);

    const tableReportFiltersAndSortData = useAppSelector(selectTableReportSortData);

    const tableReportColumnFilters = useAppSelector(selectTableReportColumnFiltersData);

    const {
        createTableColumnFilterDataGetter,
        createTableColumnFilterDataSetter,
        getTableColumnFilterInfo,
    } = useTableColumnFilters({
        tableFiltersData: tableReportColumnFilters,
        setTableFiltersData: columnFilters => {
            dispatch(setTableColumnFilters({
                filters: columnFilters,
            }));
        },
    });

    const {
        createSortDataGetter,
        createSortDataSetter,
    } = useTableColumnSort({
        tableColumnSortData: {sort: tableReportFiltersAndSortData},
        setTableColumnSortData({sort}) {
            dispatch(
                setSortData({sort}),
            );
        },
    });

    const {
        pagination,
        hiddenColumnsKeys,
        setHiddenColumns,
        hideColumn,
        loadTableReportData,
        handleRefreshReportPage,
    } = useControl({
        docId,
        templateCode,
    });

    useEffect(() => {
        handleRefreshReportPage();
    }, [organizationId, taxPeriodId]);

    const columns = useAppSelector(s => selectTableReportCurrentColumns(s, hiddenColumnsKeys));

    const {
        pushWithBreadcrumbs,
        handleBreadcrumbClick,
        defaultBreadcrumbs,
    } = useBreadcrumbsController({
        docId,
        entityName,
        templateCode,
    });

    const sublistCode = entityName ? getPrefixedSection(removeExtraPartFromEntityName(entityName)) : '';

    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [sublistCode],
    });

    const {hasUserRoleFunction: hasUserRoleFunctionWithDocId} = useUserRoleFunctionsByDocId({
        tableReportDocId: docId,
        templateCode: templateCode || null,
    });

    const hasRolePermission = (func: EUserRoleFunction) => {
        if (sublistCode && docId === null) return hasUserRoleFunction(func);
        return hasUserRoleFunctionWithDocId(func);
    };

    const handleSideMenuCollapse = () => {
        setIsMenuCollapsed(prev => !prev);
    };
    const handleScaleChange = (value: number) => {
        setScale(value);
    };

    const tableFontSize = `${14 * scale}px`;
    const headerFontSize = `${12 * scale}px`;
    const bottomPagination: TablePaginationConfig = {
        ...pagination,
        current: pagination.current + 1,
        total: pagination?.total && pagination?.total * pagination.pageSize,
        position: ['bottomLeft'],
        locale: {items_per_page: 'на странице', jump_to: ''},
        showQuickJumper: {goButton: <ArrowRightIcon className="pagination_jumper" />},
        showSizeChanger: !reservedColumns?.pageNumber,
        pageSizeOptions: defaultPageSizeOptions,
        onChange: (page: number, size?: number) => pagination.onChange(page - 1, size),
    };
    const tablePagination = paginationPosition === EReportConfigurationPosition.TOP ? false : bottomPagination;

    const {
        applyFormatSettingsToRow,
    } = tableReportColumnContentUtils;

    useEffect(() => {
        setIsTableHeaderPinned(!((dataSource && dataSource.length <= 0)));
    }, [dataSource]);

    const tableReportAttributesForFilters = useAppSelector(selectTableReportAttributes);

    useEffect(() => {
        if (isAllRowsSelected && lastRequestParams) {
            dispatch(loadTableReportPageData({
                ...lastRequestParams,
                dataParams: {
                    ...lastRequestParams.dataParams,
                    size: pagination.pageSize,
                },
                useConfigCache: true,
            }));
        }
    }, [isAllRowsSelected]);

    useAfterEffect(() => {
        if (dataSource && dataSource.length && isAllRowsSelected) {
            setSelectedRowKeys(dataSource.map(({id}) => id) as number[]);
        } else {
            setSelectedRowKeys([]);
        }
    }, [dataSource, isAllRowsSelected]);

    const filteredColumns = useMemo(() => {
        if (chosenMode === EReportViewMode.SUMMARY) {
            return columns?.filter(column => !column?.closeFieldWhenChangingMode);
        }
        return columns;
    }, [columns, chosenMode]);

    const recordsInfo = useMemo(() => (
        <RecordsInfo
            current={pagination.current + 1}
            pageSize={pagination.pageSize}
            documentsTotalCount={pagination.totalElements}
        />
    ), [pagination.current, pagination.pageSize, pagination.totalElements]);

    return (
        <div
            className="table-report"
            ref={tableRef}
        >
            <UIBlocker
                content={isFileLoading ? (
                    <Spinner
                        tip="Загрузка файла..."
                    />
                ) : undefined}
            >
                <div className="table-report__breadcrumbs">
                    <Breadcrumbs
                        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
                        onClickCallback={handleBreadcrumbClick}
                    />
                </div>

                <BreadcrumbsContext.Provider value={{
                    breadcrumbs: breadcrumbs ?? defaultBreadcrumbs,
                }}
                >
                    <div
                        className={cn('table-report__wrapper', {
                            'table-report__wrapper_loading': isLoadingPage && !(dataSource || columns),
                        })}
                    >
                        <DrillDownModal
                            entries={ddModalEntries}
                            visible={isDDModalVisible}
                            setVisible={setIsDDModalVisible}
                            pushWithBreadcrumbs={pushWithBreadcrumbs}
                        />

                        <TableReportHeader
                            designTemplate={designTemplate}
                        />

                        <TableReportActions
                            docId={docId}
                            handleMenuCollapse={handleSideMenuCollapse}
                            handleScaleChange={handleScaleChange}
                            pagination={pagination}
                            paginationPosition={paginationPosition}
                            columns={columns}
                            hiddenColumnsKeys={hiddenColumnsKeys}
                            hideColumns={setHiddenColumns}
                            dataSource={dataSource}
                            handleRefreshReportPage={handleRefreshReportPage}
                            handlePrint={handlePrint}
                            hasRolePermission={hasRolePermission}
                            isAllRowsSelected={isAllRowsSelected}
                            setIsAllRowsSelected={setIsAllRowsSelected}
                            selectedTableRows={selectedRowKeys as number[]}
                            chosenMode={chosenMode}
                            setChosenMode={setChosenMode}
                        />

                        <Spin
                            className="table-report__wrapper__spinner"
                            spinning={
                                isLoadingPage || !(dataSource || columns)
                            }
                            tip="Загрузка данных отчёта..."
                        >
                            <UIBlocker
                                thunkError={{
                                    error: loadTablePageError,
                                    retryCallback: () => {
                                        loadTableReportData({page: 0, noConfigCache: true});
                                    },
                                    goBackCallback: () => {
                                        history.go(-1);
                                    },
                                    verbose: true,
                                }}
                                label="Во время загрузки отчёта произошла ошибка"
                                abortedLabel="Загрузка данных отчёта..."
                            >
                                <Card
                                    className="table-report__content"
                                    bordered={false}
                                >
                                    {!!(enabledMenu && asideMenuStructure?.length) && (
                                        <Aside
                                            menuStructure={asideMenuStructure}
                                            collapsed={isMenuCollapsed}
                                            handleChangePage={pagination.onChange}
                                            currentPage={pagination.current}
                                        />
                                    )}
                                    <div
                                        className="flex-grow-1 table-report__table-container"
                                        style={{width: 0}}
                                    >
                                        {enabledRecordsInfo && recordsInfo}

                                        <div ref={tableContentRef}>
                                            <Table
                                                rowKey="id"
                                                className={cn(
                                                    'table-report__table',
                                                    {'d-none': isLoadingPage && !(dataSource || filteredColumns)},
                                                    {'table-report__table_pinned': isTableHeaderPinned},
                                                )}
                                                dataSource={dataSource}
                                                pagination={tablePagination}
                                                scroll={{x: '100%'}}
                                                showSorterTooltip={false}
                                                rowSelection={enabledSelectAllRowsBtn ? {
                                                    type: 'checkbox',
                                                    selectedRowKeys,
                                                    onChange: (newSelectedRowKeys: Key[]) => {
                                                        setSelectedRowKeys(newSelectedRowKeys as number[]);
                                                    },
                                                    getCheckboxProps: () => ({
                                                        disabled: isAllRowsSelected,
                                                    }),
                                                } : undefined}
                                            >
                                                {renderColumns({
                                                    headerStructure: excelTableHeaderStructure,
                                                    columns: filteredColumns,
                                                    columnProps: {
                                                        templateCode,
                                                        docId,
                                                        enabledRowAttachments,
                                                        enabledRowComments,
                                                        enabledRowRequests,
                                                        enabledSelectAllRowsBtn,
                                                        enabledDrillUpBtn,

                                                        createTableColumnFilterDataGetter,
                                                        createTableColumnFilterDataSetter,
                                                        getTableColumnFilterInfo,

                                                        createSortDataGetter,
                                                        createSortDataSetter,

                                                        headerFontSize,
                                                        tableFontSize,
                                                        hideColumn,
                                                        applyFormatSettingsToRow,
                                                        drillDowns,
                                                        pushWithBreadcrumbs,
                                                        setIsDDModalVisible,
                                                        setDDModalEntries,
                                                        rowFormats,
                                                        loadTableReportData,
                                                        tableReportAttributes,
                                                        reportGroups: userReportGroups || reportGroups,
                                                        handlePinButtonClick,
                                                        isTableHeaderPinned,
                                                        enabledPinHeaderButton: dataSource && dataSource?.length > 0,
                                                        enabledMenu,

                                                        hasRolePermission,
                                                        tableReportAttributesForFilters,
                                                    },
                                                })}
                                            </Table>
                                        </div>
                                    </div>
                                </Card>
                            </UIBlocker>
                        </Spin>
                    </div>
                </BreadcrumbsContext.Provider>
            </UIBlocker>

            <HorizontalScroll
                containerRef={tableContentRef}
                scrollableSelector={CONTAINER_SELECTORS.TABLE}
                enabled={Boolean(dataSource && dataSource.length > 0)}
            />
        </div>
    );
};
